import { useState } from "react";
import { Form, required, useNotify, useTranslate } from "ra-core";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { TextInput } from "@/components/admin/text-input";

import { getSupabaseClient } from "../providers/supabase/supabase";

type MfaSmsEnrollProps = {
  onEnrolled: (factorId: string) => void;
};

export const MfaSmsEnroll = ({ onEnrolled }: MfaSmsEnrollProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [step, setStep] = useState<"phone" | "code">("phone");

  const handlePhoneSubmit: SubmitHandler<FieldValues> = async (values) => {
    setLoading(true);
    try {
      const { data, error } = await getSupabaseClient().auth.mfa.enroll({
        factorType: "phone",
        friendlyName: translate("crm.mfa.sms.friendly_name", {
          _: "SMS",
        }),
        phone: values.phone,
      });

      if (error) {
        throw error;
      }

      setFactorId(data.id);
      setStep("code");
    } catch (error: any) {
      notify(error?.message ?? "crm.mfa.enroll_error", {
        type: "error",
        messageArgs: {
          _: "Could not send SMS code. Check the phone number and try again.",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit: SubmitHandler<FieldValues> = async (values) => {
    if (!factorId) return;

    setLoading(true);
    try {
      const { data: challenge, error: challengeError } =
        await getSupabaseClient().auth.mfa.challenge({ factorId });

      if (challengeError) {
        throw challengeError;
      }

      const { error: verifyError } = await getSupabaseClient().auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code: values.code,
      });

      if (verifyError) {
        throw verifyError;
      }

      onEnrolled(factorId);
    } catch (error: any) {
      notify(error?.message ?? "crm.mfa.verify_error", {
        type: "error",
        messageArgs: {
          _: "Invalid code. Please try again.",
        },
      });
    } finally {
      setLoading(false);
    }
  };

  if (step === "phone") {
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {translate("crm.mfa.sms.description", {
            _: "Receive one-time codes via SMS. Use international format, e.g. +4512345678.",
          })}
        </p>
        <Form onSubmit={handlePhoneSubmit}>
          <TextInput
            source="phone"
            label="crm.mfa.sms.phone"
            validate={required()}
            helperText={false}
            autoComplete="tel"
          />
          <Button type="submit" className="w-full mt-4" disabled={loading}>
            {translate("crm.mfa.sms.send_code", { _: "Send code" })}
          </Button>
        </Form>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {translate("crm.mfa.sms.code_sent", {
          _: "Enter the code we sent to your phone.",
        })}
      </p>
      <Form onSubmit={handleCodeSubmit}>
        <TextInput
          source="code"
          label="crm.mfa.code"
          validate={required()}
          helperText={false}
          autoComplete="one-time-code"
        />
        <Button type="submit" className="w-full mt-4" disabled={loading}>
          {translate("crm.mfa.verify_and_continue", {
            _: "Verify and continue",
          })}
        </Button>
      </Form>
    </div>
  );
};
